import { hasActiveFilters } from '@/lib/matrix-grid-filters';
import {
  DESTINATAIRE_FILTER_LABELS,
  MATRIX_DISPLAY_STATE_LABELS,
  type MatrixGridSettings,
} from '@/lib/matrix-grid-search';
import { MATRIX_PIVOT_LABELS } from '@mss_creator/shared';
import { Icon } from '@pms-digital/design-system';

export interface FilterChipsProps {
  settings: MatrixGridSettings;
  onChange: (next: MatrixGridSettings) => void;
  onReset: () => void;
  /** « N matrices sur M », left out while the list loads. */
  counts: { visible: number; total: number } | null;
}

interface Chip {
  key: string;
  label: string;
  clear: (settings: MatrixGridSettings) => MatrixGridSettings;
}

function activeChips(settings: MatrixGridSettings): Chip[] {
  const chips: Chip[] = [];
  const q = settings.q.trim();
  if (q !== '') {
    chips.push({ key: 'q', label: `Recherche : « ${q} »`, clear: (s) => ({ ...s, q: '' }) });
  }
  if (settings.states.length > 0) {
    chips.push({
      key: 'states',
      label: `État : ${settings.states.map((state) => MATRIX_DISPLAY_STATE_LABELS[state]).join(', ')}`,
      clear: (s) => ({ ...s, states: [] }),
    });
  }
  if (settings.pivots.length > 0) {
    chips.push({
      key: 'pivots',
      label: `Pivot : ${settings.pivots.map((pivot) => MATRIX_PIVOT_LABELS[pivot]).join(', ')}`,
      clear: (s) => ({ ...s, pivots: [] }),
    });
  }
  if (settings.destinataires.length > 0) {
    chips.push({
      key: 'destinataire',
      label: `Destinataire : ${settings.destinataires
        .map((destinataire) => DESTINATAIRE_FILTER_LABELS[destinataire])
        .join(', ')}`,
      clear: (s) => ({ ...s, destinataires: [] }),
    });
  }
  if (settings.createdBy === 'me') {
    chips.push({ key: 'by', label: 'Créée par : Moi', clear: (s) => ({ ...s, createdBy: 'all' }) });
  }
  return chips;
}

/**
 * One removable chip per active filter, « Réinitialiser » (the filters only:
 * the sort and the view stay) and the « N matrices sur M » count. Renders
 * nothing while no filter is active.
 */
export function FilterChips({ settings, onChange, onReset, counts }: FilterChipsProps) {
  if (!hasActiveFilters(settings)) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 text-caption">
      {activeChips(settings).map((chip) => (
        <span
          key={chip.key}
          className="inline-flex h-7 items-center gap-1 rounded-full bg-bleu-nuit-tint pr-1 pl-2 font-semibold text-bleu-nuit"
        >
          {chip.label}
          <button
            type="button"
            aria-label={`Retirer le filtre ${chip.label}`}
            onClick={() => onChange(chip.clear(settings))}
            className="inline-flex size-[18px] items-center justify-center rounded-full hover:bg-bleu-nuit hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            <Icon name="close" size={14} />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onReset}
        className="font-semibold text-bleu-nuit underline underline-offset-2"
      >
        Réinitialiser
      </button>
      {counts && (
        <span className="text-gray-600">
          · {counts.visible} {counts.visible > 1 ? 'matrices' : 'matrice'} sur {counts.total}
        </span>
      )}
    </div>
  );
}
